import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import BulkDeleteModal from "../components/BulkDeleteModal";
import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

const STATUSES = ["draft", "open", "shortlisting", "filled", "closed"];

export default function AdminMissions() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [posterType, setPosterType] = useState("");
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // bulk delete state
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (status) params.status = status;
      if (posterType) params.poster_type = posterType;
      if (q) params.q = q;
      const { data } = await api.get("/admin/missions", { params });
      setRows(data);
      setSelected({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setLoading(false); }
  }, [status, posterType, q, t]);

  useEffect(() => { load(); }, [load]);

  const forceClose = async () => {
    if (!target || !reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/missions/${target.id}/force-close`, { reason: reason.trim() });
      toast.success(t("admin_missions.closed"));
      setTarget(null);
      setReason("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setBusy(false); }
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedItems = useMemo(
    () => rows.filter((r) => selected[r.id]).map((r) => ({ id: r.id, label: r.title })),
    [rows, selected]
  );
  const allChecked = rows.length > 0 && selectedIds.length === rows.length;
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else {
      const next = {};
      rows.forEach((r) => { next[r.id] = true; });
      setSelected(next);
    }
  };
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/missions/bulk-delete", { ids: selectedIds });
      toast.success(t("admin_missions.bulk_deleted_toast", {
        missions: data.deleted_missions ?? 0,
        subs: data.deleted_submissions ?? 0,
        msgs: data.deleted_messages ?? 0,
      }));
      setBulkOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-missions-page">
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("admin_missions.eyebrow")}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-testid="admin-missions-title">{t("admin_missions.title")}</h1>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input placeholder={t("admin_missions.search_placeholder")} value={q} onChange={(e) => setQ(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-missions-q" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-missions-status">
            <option value="">{t("admin_missions.all_statuses")}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={posterType} onChange={(e) => setPosterType(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-missions-poster">
            <option value="">{t("admin_missions.all_posters")}</option>
            <option value="employer">employer</option>
            <option value="agency">agency</option>
          </select>
          {selectedIds.length > 0 && (
            <button
              onClick={() => setBulkOpen(true)}
              className="ml-auto rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              data-testid="admin-missions-bulk-delete-btn"
            >
              {t("admin_bulk.delete_n_items", { count: selectedIds.length })}
            </button>
          )}
        </div>
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {loading ? <p className="text-sm text-slate-500 p-2">{t("common.loading")}</p> : rows.length === 0 ? (
            <p className="text-sm text-slate-500 p-2" data-testid="admin-missions-empty">{t("admin_missions.empty")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 pl-1 w-8 text-left">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      data-testid="admin-missions-select-all"
                    />
                  </th>
                  <th className="py-2 text-left">{t("admin_missions.col_title")}</th>
                  <th className="py-2 text-left">{t("admin_missions.col_poster")}</th>
                  <th className="py-2 text-left">{t("admin_missions.col_status")}</th>
                  <th className="py-2 text-left">{t("admin_missions.col_subs")}</th>
                  <th className="py-2 text-left">{t("admin_missions.col_date")}</th>
                  <th className="py-2 text-right">{t("admin_missions.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100" data-testid={`admin-mission-row-${m.id}`}>
                    <td className="py-2 pl-1">
                      <input
                        type="checkbox"
                        checked={!!selected[m.id]}
                        onChange={() => toggleOne(m.id)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        data-testid={`admin-mission-select-${m.id}`}
                      />
                    </td>
                    <td className="py-2 text-slate-900">
                      <Link to={`/missions/${m.id}`} className="hover:text-indigo-700">{m.title}</Link>
                      {m.admin_note && <span className="ml-2 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-rose-100" title={m.admin_note}>{t("admin_missions.admin_note")}</span>}
                    </td>
                    <td className="py-2 text-slate-700">{m.poster_name} <span className="text-xs text-slate-500">({m.poster_type})</span></td>
                    <td className="py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">{m.status}</span></td>
                    <td className="py-2 text-slate-600">{m.submissions_count}</td>
                    <td className="py-2 text-slate-500">{formatDateShort(m.created_at)}</td>
                    <td className="py-2 text-right">
                      {m.status !== "closed" && m.status !== "filled" && (
                        <button
                          onClick={() => { setTarget(m); setReason(""); }}
                          className="rounded border border-rose-200 bg-white px-2 py-0.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                          data-testid={`admin-mission-close-${m.id}`}
                        >
                          {t("admin_missions.force_close")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {target && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="admin-force-close-modal">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900">{t("admin_missions.close_modal_title", { title: target.title })}</h3>
              <p className="mt-1 text-xs text-slate-500">{t("admin_missions.close_modal_hint")}</p>
              <label className="mt-4 block text-sm font-medium text-slate-700">{t("admin_missions.reason")}</label>
              <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-close-reason" />
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setTarget(null)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="admin-close-cancel">
                  {t("common.cancel")}
                </button>
                <button onClick={forceClose} disabled={busy || !reason.trim()} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60" data-testid="admin-close-confirm">
                  {busy ? t("common.loading") : t("admin_missions.force_close")}
                </button>
              </div>
            </div>
          </div>
        )}

        <BulkDeleteModal
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onConfirm={doBulkDelete}
          busy={bulkBusy}
          items={selectedItems}
          kind="missions"
          warning={t("admin_bulk.warning_missions")}
        />
      </main>
    </div>
  );
}
